import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';
import { Avatar } from 'src/game/components/shared/Avatar';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Feature } from 'src/replicant/analytics';
import { WeeklyLeaderboardMemberData } from '../weeklyLeaderboard.utils';
import { LeaderboardsScore } from './LeaderboardsScore';
import { user } from 'src/StateObserver';

type LeaderboardsPodiumEntryOpts = ViewOpts & {
  onRewardClick?: () => Promise<void>;
};

export class LeaderboardsPodiumEntry extends View {
  private readonly podium: ImageScaleView;
  private readonly avatar: Avatar;
  private readonly name: LangBitmapFontTextView;
  private readonly medal: ImageScaleView;
  public readonly chest: ButtonScaleView;
  private readonly shine: ImageScaleView;
  private readonly score: LeaderboardsScore;
  private readonly userContent: View;
  public position?: number;

  constructor(opts: LeaderboardsPodiumEntryOpts) {
    super(opts);

    this.podium = new ImageScaleView({
      superview: this,
      scaleMethod: 'contain',
    });

    this.userContent = new View({
      superview: this.podium,
      width: this.style.width,
      height: this.style.height,
      zIndex: 1,
    });

    this.avatar = new Avatar({
      superview: this.userContent,
      width: 60,
      height: 60,
      x: 50,
      y: -10,
      centerOnOrigin: true,
    });

    this.medal = new ImageScaleView({
      superview: this.userContent,
      width: downscale(81),
      height: downscale(92),
      centerOnOrigin: true,
      scaleMethod: 'contain',
      x: this.style.width / 2,
      y: this.style.height - 15,
    });

    this.chest = new ButtonScaleView({
      superview: this.userContent,
      width: downscale(195),
      height: downscale(182),
      centerOnOrigin: true,
      scaleMethod: 'contain',
      x: this.style.width / 2,
      y: this.style.height / 2 - 60,
      onClick: opts.onRewardClick,
      tracking: {
        feature: Feature.WEEKLY_LEADERBOARD._,
        $subFeature: Feature.WEEKLY_LEADERBOARD.BUTTON,
      },
    });

    this.shine = new ImageScaleView({
      superview: this.userContent,
      image:
        'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/chest_shine.png',
      width: downscale(240),
      height: downscale(198),
      centerOnOrigin: true,
      scaleMethod: 'contain',
      zIndex: -1,
      x: this.style.width / 2,
      y: this.style.height / 2 - 40,
    });

    this.name = new LangBitmapFontTextView({
      superview: this.userContent,
      x: 85,
      y: -40,
      align: 'left',
      verticalAlign: 'center',
      size: 24,
      font: 'Title',
      width: 140,
      height: 40,
      color: '#FFFFFF',
    });

    this.score = new LeaderboardsScore({
      superview: this.podium,
      width: downscale(190),
      height: downscale(77),
      centerOnOrigin: true,
      x: this.medal.style.x + 15,
      y: this.medal.style.y - 60,
    });
  }

  public setup(position: number, data?: WeeklyLeaderboardMemberData) {
    if (!data) {
      this.userContent.hide();
      this.score.hide();
    } else {
      this.userContent.show();
      this.score.show();

      const { score, name, photo, frame } = data;

      this.name.localeText = () => name;
      this.score.label.localeText = () => (score ?? 0).toString();
      this.avatar.updateImage(photo);
      this.avatar.setFrame(frame);
      this.position = position;

      if (data.id === user().id) {
        this.name.updateOpts({
          color: '#FFCC00',
        });
      } else {
        this.name.updateOpts({
          color: '#FFFFFF',
        });
      }
    }

    //Set specific player entry attributes for Top 3 players
    if (position === 1) {
      this.chest.updateOpts({
        image: `assets/ui/weeklyLeaderboard/popups/popupLeaderboards/treasure_1.png`,
        width: downscale(152),
        height: downscale(145),
        y: this.style.height / 2 - 40,
        scaleMethod: 'contain',
        centerOnOrigin: true,
      });
      this.podium.updateOpts({
        image:
          'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/podium_1.png',
        width: downscale(318),
        height: downscale(402),
        scaleMethod: 'contain',
      });
      this.score.updateOpts({
        image:
          'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/panel_list_inset_gold.png',
      });
    } else if (position === 2) {
      this.chest.updateOpts({
        image: `assets/ui/weeklyLeaderboard/popups/popupLeaderboards/treasure_2.png`,
        width: downscale(111),
        height: downscale(117),
        y: this.style.height / 2 - 40,
        scaleMethod: 'contain',
        centerOnOrigin: true,
      });
      this.podium.updateOpts({
        image:
          'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/podium_2.png',
        width: downscale(317),
        height: downscale(356),
        scaleMethod: 'contain',
      });
      this.score.updateOpts({
        image:
          'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/panel_list_inset_silver.png',
      });
    } else if (position === 3) {
      this.chest.updateOpts({
        image: `assets/ui/weeklyLeaderboard/popups/popupLeaderboards/treasure_3.png`,
        width: downscale(96),
        height: downscale(106),
        y: this.style.height / 2 - 45,
        scaleMethod: 'contain',
        centerOnOrigin: true,
      });
      this.podium.updateOpts({
        image:
          'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/podium_3.png',
        width: downscale(317),
        height: downscale(314),
        scaleMethod: 'contain',
      });
      this.score.updateOpts({
        image:
          'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/panel_list_inset_bronze.png',
      });
    }

    this.medal.updateOpts({
      image: `assets/ui/weeklyLeaderboard/popups/popupLeaderboards/leaderboards_medal_${position}.png`,
    });
    this.shine.updateOpts({
      visible: position === 1,
    });
  }
}
